import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Flag } from './Flag';
import { useLanguage } from '../context/LanguageContext';
import { HOME_PATHS } from '../config/landings';
import { Menu, X, Globe, GraduationCap } from 'lucide-react';
import { trackTrialCtaClick } from '../lib/analytics';

const TEST_LABEL = { es: 'Test de nivel', de: 'Einstufungstest', en: 'Level test' };
const TEST_BADGE = { es: 'GRATIS', de: 'GRATIS', en: 'FREE' };
const TEST_TITLE = {
  es: 'Test de nivel de español gratis',
  de: 'Kostenloser Spanisch-Einstufungstest',
  en: 'Free Spanish level test',
};

export const Navigation = () => {
  const { language, changeLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const langHome = HOME_PATHS[language] || '/';
    // Desde una subpágina volvemos a la home DEL MISMO IDIOMA con el ancla
    if (location.pathname.replace(/\/+$/, '') !== (langHome === '/' ? '' : langHome)) {
      navigate(`${langHome === '/' ? '' : langHome}/#${id}`);
      setIsMobileMenuOpen(false);
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const goToPath = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const languages = [
    { code: 'es', flag: 'es', name: 'Español' },
    { code: 'en', flag: 'gb', name: 'English' },
    { code: 'de', flag: 'de', name: 'Deutsch' }
  ];

  const currentLang = languages.find(lang => lang.code === language);

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-[#C8E7F2]/95 backdrop-blur-md shadow-lg'
          : 'bg-[#C8E7F2]/85 backdrop-blur-sm shadow-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center gap-4 h-20">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="flex items-center space-x-3 cursor-pointer flex-shrink-0"
            onClick={() => scrollToSection('hero')}
          >
            <img src="/logo.png" alt="Spanisch en la red" className="h-14 w-14 rounded-full object-cover shadow-md" />
            <span className="sr-only" style={{ color: '#F5A6A4' }}>
              SPANISCH EN LA RED
            </span>
          </motion.div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-2.5 xl:gap-4 2xl:gap-5 min-w-0">
            {[
              { label: t.nav.home, id: 'hero' },
              { label: t.nav.about, id: 'about' },
              { label: t.nav.methodology, id: 'methodology' },
              { label: t.nav.pricing || 'Tarifas', id: 'pricing' },
              { label: t.nav.resources, id: 'resources' },
              { label: t.nav.blog || 'Blog', path: '/blog' },
              { label: t.nav.podcast, id: 'podcast' },
              { label: t.nav.testimonials, id: 'testimonials' },
              { label: t.nav.contact, id: 'footer' }
            ].map((item, index) => (
              <motion.button
                key={item.id || item.path}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => item.path ? goToPath(item.path) : scrollToSection(item.id)}
                whileHover={{ y: -2 }}
                data-testid={`nav-${item.id || item.path.replace('/', '')}`}
                className="text-sm font-medium text-[#4A4A4A] hover:text-[#A8D8EA] transition-colors whitespace-nowrap"
              >
                {item.label}
              </motion.button>
            ))}

            {/* Language Selector */}
            <div className="relative">
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6 }}
                onClick={() => setShowLangMenu(!showLangMenu)}
                data-testid="language-selector"
                className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-[#C8E7F2] hover:bg-[#A8D8EA] transition-colors duration-200 flex-shrink-0 whitespace-nowrap"
              >
                <Globe className="w-4 h-4 text-[#4A4A4A]" />
                <span className="text-sm font-medium text-[#4A4A4A] flex items-center gap-1.5"><Flag code={currentLang.flag} title={currentLang.name} />{currentLang.code.toUpperCase()}</span>
              </motion.button>

              <AnimatePresence>
                {showLangMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden"
                  >
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          changeLanguage(lang.code);
                          setShowLangMenu(false);
                        }}
                        data-testid={`language-option-${lang.code}`}
                        className={`w-full text-left px-4 py-2 hover:bg-[#C8E7F2] transition-colors flex items-center space-x-2 ${
                          language === lang.code ? 'bg-[#C8E7F2] text-[#4A4A4A]' : 'text-gray-700'
                        }`}
                      >
                        <Flag code={lang.flag} title={lang.name} />
                        <span className="text-sm">{lang.name}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Test de nivel — highlighted CTA (only on XL to avoid crowding) */}
            <Link
              to="/test-de-nivel"
              data-testid="nav-test-de-nivel"
              className={`${language === 'es' ? 'hidden 2xl:inline-flex' : 'hidden'} items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-[#F5A6A4] to-[#A8D8EA] text-white font-bold text-sm shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all whitespace-nowrap`}
              title={TEST_TITLE[language] || TEST_TITLE.es}
            >
              <GraduationCap className="w-4 h-4" />
              {TEST_LABEL[language] || TEST_LABEL.es}
              <span className="text-[10px] font-extrabold bg-white/25 rounded px-1.5 py-0.5 ml-1 tracking-wider">{TEST_BADGE[language] || TEST_BADGE.es}</span>
            </Link>

            {/* CTA Button */}
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7 }}
              onClick={() => { trackTrialCtaClick('nav'); scrollToSection('booking'); }}
              className="whitespace-nowrap px-4 xl:px-6 py-2.5 text-sm bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-full font-medium hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              {t.nav.cta}
            </motion.button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-gray-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-gray-100"
          >
            <div className="px-4 py-4 space-y-3">
              {[
                { label: t.nav.home, id: 'hero' },
                { label: t.nav.about, id: 'about' },
                { label: t.nav.methodology, id: 'methodology' },
                { label: t.nav.pricing || 'Tarifas', id: 'pricing' },
                { label: t.nav.resources, id: 'resources' },
                { label: t.nav.blog || 'Blog', path: '/blog' },
                { label: t.nav.podcast, id: 'podcast' },
                { label: t.nav.testimonials, id: 'testimonials' },
                { label: t.nav.contact, id: 'footer' }
              ].map((item) => (
                <button
                  key={item.id || item.path}
                  onClick={() => item.path ? goToPath(item.path) : scrollToSection(item.id)}
                  data-testid={`mobile-nav-${item.id || item.path.replace('/', '')}`}
                  className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-[#C8E7F2]/60 hover:text-[#A8D8EA] rounded-lg transition-colors duration-200"
                >
                  {item.label}
                </button>
              ))}

              <div className="flex space-x-2 pt-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      changeLanguage(lang.code);
                      setIsMobileMenuOpen(false);
                    }}
                    data-testid={`mobile-language-option-${lang.code}`}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      language === lang.code
                        ? 'bg-[#A8D8EA] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Flag code={lang.flag} title={lang.name} className="mr-1.5" />{lang.code.toUpperCase()}
                  </button>
                ))}
              </div>

              {language === 'es' && (
              <Link
                to="/test-de-nivel"
                data-testid="mobile-nav-test-de-nivel"
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#F5A6A4] to-[#A8D8EA] text-white rounded-full font-bold shadow-md hover:shadow-lg transition-all"
              >
                <GraduationCap className="w-4 h-4" />
                {TEST_LABEL[language] || TEST_LABEL.es} · {TEST_BADGE[language] || TEST_BADGE.es}
              </Link>
              )}

              <button
                onClick={() => { trackTrialCtaClick('nav_mobile'); scrollToSection('booking'); }}
                className="w-full px-6 py-3 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-full font-medium hover:shadow-lg transition-all duration-300"
              >
                {t.nav.cta}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};